import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, History, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api, errMsg, runJob } from "../lib/api";
import { QuestionResult } from "../components/MarkingResults";

const markOptions = [
  { v: "0", label: "Mixed set (2–16 marks)" },
  { v: "2", label: "2-mark questions" },
  { v: "4", label: "4-mark questions" },
  { v: "6", label: "6-mark questions" },
  { v: "8", label: "8-mark questions" },
  { v: "16", label: "16-mark essays" },
];

const countOptions = ["4", "6", "8", "10"];

const QuestionCard = ({ q, setId, onMarked }) => {
  const [show, setShow] = useState(null);
  const [answer, setAnswer] = useState(q.marking?.student_answer || "");
  const [marking, setMarking] = useState(false);

  const submitMark = async () => {
    if (!answer.trim()) {
      toast.error("Write an answer first");
      return;
    }
    setMarking(true);
    try {
      await runJob(() => api.post("/questions/mark", { set_id: setId, number: q.number, answer }));
      await onMarked();
      toast.success("Answer marked!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setMarking(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-background p-5" data-testid={`gen-question-${q.number}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-sm font-semibold font-display">Question {q.number}</span>
        <div className="flex gap-1.5 flex-wrap justify-end">
          <Badge variant="outline" className="text-xs">{q.command_word}</Badge>
          <Badge className="bg-primary/15 text-primary hover:bg-primary/15 text-xs">{q.marks} marks</Badge>
        </div>
      </div>
      {q.subtopic && (
        <Badge variant="secondary" className="text-[11px] mb-2" data-testid={`gen-question-subtopic-${q.number}`}>{q.subtopic}</Badge>
      )}
      <p className="text-sm leading-relaxed whitespace-pre-line">{q.text}</p>

      {/* Answer + mark */}
      <div className="mt-4">
        <Textarea
          data-testid={`answer-input-${q.number}`}
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="Write your answer here, then mark it against the mark scheme…"
          className="min-h-[110px] text-sm"
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full glow-btn text-xs h-8" data-testid={`mark-answer-btn-${q.number}`}
            disabled={marking} onClick={submitMark}>
            {marking ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Marking…</> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Mark my answer</>}
          </Button>
          <Button size="sm" variant={show === "ms" ? "default" : "outline"} className="rounded-full text-xs h-8"
            data-testid={`toggle-ms-${q.number}`} onClick={() => setShow(show === "ms" ? null : "ms")}>Mark scheme</Button>
          <Button size="sm" variant={show === "ma" ? "default" : "outline"} className="rounded-full text-xs h-8"
            data-testid={`toggle-ma-${q.number}`} onClick={() => setShow(show === "ma" ? null : "ma")}>Model answer</Button>
        </div>
      </div>

      {show && (
        <p className="mt-3 text-xs text-muted-foreground leading-relaxed whitespace-pre-line rounded-xl bg-secondary p-4">
          {show === "ms" ? q.mark_scheme : q.model_answer}
        </p>
      )}

      {q.marking && (
        <div className="mt-4" data-testid={`answer-marking-${q.number}`}>
          <QuestionResult r={q.marking} />
        </div>
      )}
    </div>
  );
};

export default function TopicDetail() {
  const { topic } = useParams();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [genMarks, setGenMarks] = useState("0");
  const [genCount, setGenCount] = useState("6");
  const [generating, setGenerating] = useState(false);
  const [resultSet, setResultSet] = useState(null);
  const [sets, setSets] = useState([]);

  const loadSets = () => api.get("/questions/sets", { params: { topic } }).then((r) => setSets(r.data)).catch(() => {});

  useEffect(() => {
    setData(null);
    setResultSet(null);
    setSelected([]);
    api.get(`/topics/${encodeURIComponent(topic)}`).then((r) => setData(r.data)).catch(() => {});
    loadSets();
  }, [topic]);

  const toggle = (sub) =>
    setSelected((prev) => (prev.includes(sub) ? prev.filter((s) => s !== sub) : [...prev, sub]));

  const generate = async () => {
    if (selected.length === 0) {
      toast.error("Select at least one specification area");
      return;
    }
    setGenerating(true);
    setResultSet(null);
    try {
      const m = parseInt(genMarks || "0", 10);
      const cnt = parseInt(genCount || "6", 10);
      const payload =
        selected.length === 1
          ? { topic, subtopic: selected[0], marks: m, count: cnt }
          : { topic, subtopics: selected, marks: m, count: cnt };
      const job = await runJob(() => api.post("/questions/generate", payload));
      const { data: qs } = await api.get(`/questions/sets/${job.result_id}`);
      setResultSet(qs);
      loadSets();
      toast.success("Questions generated!");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGenerating(false);
    }
  };

  const refetchSet = async () => {
    if (!resultSet?.id) return;
    const { data: qs } = await api.get(`/questions/sets/${resultSet.id}`);
    setResultSet(qs);
  };

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const s = data.stats || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="topic-detail-page">
      <Link to="/topics" data-testid="back-to-topics-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> All Topics
      </Link>
      <h1 className="text-4xl font-bold tracking-tighter" data-testid="topic-title">{data.topic}</h1>

      <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
        {[
          { label: "Specification areas", value: data.subtopics.length },
          { label: "Questions in analysed papers", value: s.appearances || 0 },
        ].map((x) => (
          <div key={x.label} className="rounded-2xl border border-border bg-card px-5 py-4" data-testid={`topic-stat-${x.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="text-2xl font-bold font-display">{x.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{x.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="text-lg font-semibold tracking-tight mb-1">Specification areas</h2>
          <p className="text-xs text-muted-foreground mb-4">Official AQA 7182 subtopics — tick one or more, then generate a mixed practice set spread across them.</p>

          {/* Generation control */}
          <div className="rounded-2xl border border-border bg-card p-4 mb-4" data-testid="generation-control">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium" data-testid="selected-count">
                {selected.length} selected
              </span>
              <div className="flex gap-2">
                <button className="text-xs text-primary hover:underline" data-testid="select-all-btn"
                  onClick={() => setSelected([...data.subtopics])}>Select all</button>
                <button className="text-xs text-muted-foreground hover:underline" data-testid="clear-selection-btn"
                  onClick={() => setSelected([])}>Clear</button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select value={genCount} onValueChange={setGenCount}>
                <SelectTrigger className="sm:w-32" data-testid="question-count-select"><SelectValue /></SelectTrigger>
                <SelectContent>{countOptions.map((o) => <SelectItem key={o} value={o}>{o} questions</SelectItem>)}</SelectContent>
              </Select>
              <Select value={genMarks} onValueChange={setGenMarks}>
                <SelectTrigger className="sm:w-52" data-testid="marks-select"><SelectValue /></SelectTrigger>
                <SelectContent>{markOptions.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" className="rounded-full glow-btn disabled:opacity-40 disabled:saturate-50" data-testid="generate-questions-btn"
                disabled={generating || selected.length === 0} onClick={generate}>
                {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate Questions</>}
              </Button>
            </div>
          </div>

          <div className="space-y-2" data-testid="subtopic-list">
            {data.subtopics.map((sub, i) => (
              <label key={i} data-testid={`subtopic-row-${i}`}
                className="flex items-start gap-3 rounded-2xl border border-border bg-card px-4 py-3 cursor-pointer hover:border-primary/50 transition-colors">
                <Checkbox className="mt-0.5" checked={selected.includes(sub)} onCheckedChange={() => toggle(sub)}
                  data-testid={`subtopic-checkbox-${i}`} />
                <span className="text-sm leading-snug">{sub}</span>
              </label>
            ))}
          </div>

          {data.past_questions.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold tracking-tight mb-1 flex items-center gap-2"><History className="h-4 w-4 text-primary" />Question history</h2>
              <p className="text-xs text-muted-foreground mb-4">Real questions asked on this topic in analysed papers.</p>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid="past-questions-list">
                {data.past_questions.map((q, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-4">
                    <p className="text-xs leading-relaxed">{q.question_text}</p>
                    <p className="mt-2 text-[11px] text-muted-foreground">{q.session} {q.year} • Paper {q.paper} • {q.command_word} • {q.marks} marks{q.origin === "seed" ? " • demo data" : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <h2 className="text-lg font-semibold tracking-tight mb-1">Generated questions</h2>
          <p className="text-xs text-muted-foreground mb-4">Original AQA-style questions — write an answer under each and mark it against the mark scheme.</p>
          {generating && (
            <div className="rounded-3xl border border-border bg-card p-10 text-center" data-testid="questions-loading">
              <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-3" />
              <p className="text-sm font-medium">Writing original questions…</p>
              <p className="text-xs text-muted-foreground mt-1">{selected.join(", ")}</p>
            </div>
          )}
          {!generating && resultSet && (
            <div className="space-y-4" data-testid="generated-questions">
              <p className="text-xs text-muted-foreground rounded-xl bg-secondary p-3">{resultSet.subtopic}</p>
              {resultSet.questions.map((q) => <QuestionCard key={`${resultSet.id}-${q.number}`} q={q} setId={resultSet.id} onMarked={refetchSet} />)}
            </div>
          )}
          {!generating && !resultSet && (
            <div className="rounded-3xl border border-dashed border-border p-10 text-center">
              <p className="text-sm text-muted-foreground">Tick one or more specification areas on the left, choose how many questions and a mark tariff, then hit <span className="text-primary font-medium">Generate Questions</span>.</p>
            </div>
          )}

          {sets.length > 0 && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold tracking-tight mb-3">Your saved sets for this topic</h3>
              <div className="space-y-2" data-testid="saved-sets-list">
                {sets.map((qs) => (
                  <button key={qs.id} data-testid={`saved-set-${qs.id}`} onClick={() => setResultSet(qs)}
                    className="w-full text-left rounded-xl border border-border bg-card p-3.5 hover:border-primary/50 transition-colors">
                    <p className="text-xs font-medium truncate">{qs.subtopic}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {qs.questions.length} questions • {qs.marks_requested ? `${qs.marks_requested}-mark` : "Mixed"} • {new Date(qs.created_at).toLocaleDateString()}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
